import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useProjectsAPI } from '../hooks/useProjectsAPI';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import { UploadSimple, MapPin, Trash, House as HouseIcon, Plus, PencilSimple, Stack, X, Camera, CircleNotch } from '@phosphor-icons/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { compressImage } from '../lib/imageCompress';
import { toast } from 'sonner';
import EmptyState from '../components/EmptyState';
import PhotoAnnotator from '../components/PhotoAnnotator';
import PinLegend from '../components/PinLegend';
import { severityRingColor } from '../components/PinMarker';

export default function Floorplan() {
    const { user } = useAuth();
    const store = useProjectsAPI(!!user?.user_id);
    const navigate = useNavigate();
    const location = useLocation();
    const [projectId, setProjectId] = useState(null);
    const [activeFloorId, setActiveFloorId] = useState(null);
    const [placing, setPlacing] = useState(false);
    const [selectedSnagId, setSelectedSnagId] = useState(null);
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [newPinDraft, setNewPinDraft] = useState(null); // { snagId } after an auto-created pin, opens the details modal
    const [highlightPinId, setHighlightPinId] = useState(null); // Pin id to pulse briefly on "Show pin" deep-link
    const imgWrap = useRef(null);
    const addFileInput = useRef(null);
    const handledEditState = useRef(false);

    useEffect(() => {
        if (store.ready && !projectId && store.state.projects.length) {
            setProjectId(store.state.projects[0].id);
        }
    }, [store.ready, store.state.projects, projectId]);

    const project = useMemo(
        () => store.state.projects.find(p => p.id === projectId),
        [store.state.projects, projectId]
    );

    // Keep activeFloorId in sync with current project
    useEffect(() => {
        if (!project) return;
        const floors = project.floorplans || [];
        if (floors.length === 0) { setActiveFloorId(null); return; }
        if (!floors.find(f => f.id === activeFloorId)) {
            setActiveFloorId(floors[0].id);
        }
    }, [project, activeFloorId]);

    // "Edit pin location" / "Show pin" handoff from the Defects tab
    // (ProjectDetail.jsx). editPinForSnagId puts the plan in placement
    // mode; showPinForSnagId just switches to the right floor + briefly
    // pulses the pin so the user can find it without entering edit.
    useEffect(() => {
        if (handledEditState.current) return;
        const s = location.state;
        if (!s || (!s.editPinForSnagId && !s.showPinForSnagId)) return;
        if (!store.ready || !store.state.projects.length) return;

        handledEditState.current = true;
        if (s.projectId) setProjectId(s.projectId);
        const targetId = s.editPinForSnagId || s.showPinForSnagId;
        const targetProject = store.state.projects.find(p => p.id === s.projectId);
        const targetSnag = targetProject?.snags.find(sn => sn.id === targetId);
        if (targetSnag?.pin?.floorId) setActiveFloorId(targetSnag.pin.floorId);

        if (s.editPinForSnagId) {
            // Edit mode — user wants to reposition the pin.
            setSelectedSnagId(s.editPinForSnagId);
            setPlacing(true);
        } else {
            // Read-only "Show pin" — highlight then let the user tap
            // the pin themselves for details. Scroll the plan into view
            // and pulse the target pin via `highlightPinId` state that
            // the SVG layer picks up.
            setHighlightPinId(s.showPinForSnagId);
            setTimeout(() => setHighlightPinId(null), 2400);
            // Give React a tick to render then scroll to the plan area.
            requestAnimationFrame(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        }
        // Clear the state so a page reload doesn't re-trigger.
        window.history.replaceState({}, '');
    }, [location.state, store.ready, store.state.projects]);

    if (!store.ready) return null;
    if (store.state.projects.length === 0) {
        return (
            <div className="min-h-screen bg-white pb-24">
                <AppHeader title="Floorplan" />
                <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
                    <EmptyState
                        icon={HouseIcon}
                        eyebrow="Plan view"
                        title="Create a property first"
                        body="Floorplans live inside a property. Once you've added one, come back here to upload your plans and pin defects exactly where they are."
                        cta={{
                            label: 'Go to properties',
                            onClick: () => navigate('/app'),
                            testId: 'empty-floorplan-go-properties',
                        }}
                        testId="empty-floorplan-no-project"
                    />
                </main>
                <BottomNav />
            </div>
        );
    }
    if (!project) return null;

    const floors = project.floorplans || [];
    const activeFloor = floors.find(f => f.id === activeFloorId) || null;

    const suggestName = () => {
        const defaults = ['Ground floor', 'First floor', 'Second floor', 'Loft', 'Basement', 'Garage'];
        const used = new Set(floors.map(f => f.name.toLowerCase()));
        const pick = defaults.find(d => !used.has(d.toLowerCase()));
        return pick || `Floor ${floors.length + 1}`;
    };

    const uploadFloor = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        e.target.value = '';
        const reader = new FileReader();
        reader.onload = () => {
            const created = store.addFloorplan(project.id, suggestName(), reader.result);
            setActiveFloorId(created.id);
        };
        reader.readAsDataURL(f);
    };

    const handlePlace = async (e) => {
        if (!placing || !activeFloor) return;
        const rect = imgWrap.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        if (selectedSnagId) {
            // Moving / setting pin for an existing snag.
            const snag = project.snags.find(s => s.id === selectedSnagId);
            const patch = { pin: { x, y, floorId: activeFloor.id } };
            // If the snag never had a pin number before, assign the next one now.
            if (snag && !snag.pinNumber) {
                const next = project.snags.reduce((m, s) => Math.max(m, s.pinNumber || 0), 0) + 1;
                patch.pinNumber = next;
            }
            await store.updateSnag(project.id, selectedSnagId, patch);
            setSelectedSnagId(null);
            setPlacing(false);
        } else {
            // Creating a new pin-originated snag. Don't guess the room — let the
            // user pick in the modal. Pin number is the next stable index.
            const next = project.snags.reduce((m, s) => Math.max(m, s.pinNumber || 0), 0) + 1;
            setPlacing(false);
            const created = await store.addSnag(project.id, {
                title: `Defect at pin ${next}`,
                roomId: null,
                pin: { x, y, floorId: activeFloor.id },
                pinNumber: next,
            });
            // `created` will be undefined if the backend 402'd — in that case
            // the Upgrade modal has already been shown via handle402() and we
            // bail quietly.
            if (created?.id) setNewPinDraft({ snagId: created.id });
        }
    };

    const commitRename = () => {
        if (renamingId && renameValue.trim()) {
            store.renameFloorplan(project.id, renamingId, renameValue.trim());
        }
        setRenamingId(null);
        setRenameValue('');
    };

    const deleteActive = () => {
        if (!activeFloor) return;
        const t = toast(`Delete "${activeFloor.name}"?`, {
            description: 'Any pins on this floor will be unpinned but the defects themselves are kept.',
            duration: 10000,
            action: {
                label: 'Delete',
                onClick: () => store.deleteFloorplan(project.id, activeFloor.id),
            },
            cancel: { label: 'Cancel', onClick: () => toast.dismiss(t) },
        });
    };

    const pinnedOnActive = project.snags.filter(s => s.pin && s.pin.floorId === activeFloorId);
    const unpinned = project.snags.filter(s => !s.pin);
    const totalPinned = project.snags.filter(s => s.pin).length;

    return (
        <div className="min-h-screen bg-white pb-24">
            <AppHeader title="Floorplan" subtitle={project.name} />

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-5">
                {store.state.projects.length > 1 && (
                    <div className="mb-4">
                        <div className="label-tiny mb-2">Property</div>
                        <select
                            data-testid="floorplan-project-select"
                            value={projectId}
                            onChange={(e) => { setProjectId(e.target.value); setActiveFloorId(null); }}
                            className="w-full border border-[#18181B] px-3 py-2 bg-white"
                        >
                            {store.state.projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                )}

                {/* Floor tabs */}
                <div className="flex items-center gap-2 mb-3">
                    <div className="label-tiny flex items-center gap-1"><Stack size={12} weight="bold" /> Floors ({floors.length})</div>
                    <button
                        onClick={() => addFileInput.current?.click()}
                        data-testid="add-floor-btn"
                        className="ml-auto inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] border border-[#18181B] px-2 py-1 hover:bg-[#F4F4F5] active:scale-95 transition-all duration-150"
                    >
                        <Plus size={12} weight="bold" /> Add floor
                    </button>
                    <input ref={addFileInput} data-testid="floorplan-file-input" type="file" accept="image/*" className="hidden" onChange={uploadFloor} />
                </div>

                {floors.length > 0 && (
                    <div className="flex gap-0 overflow-x-auto border border-[#18181B] mb-4">
                        {floors.map(f => (
                            <button
                                key={f.id}
                                onClick={() => setActiveFloorId(f.id)}
                                data-testid={`floor-tab-${f.id}`}
                                className={`flex-none px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] border-r border-[#18181B] last:border-r-0 ${activeFloorId === f.id ? 'bg-[#1FA5CF] text-white' : 'bg-white text-[#09090B] hover:bg-[#F4F4F5]'}`}
                            >
                                {f.name}
                            </button>
                        ))}
                    </div>
                )}

                {activeFloor && (
                    <div className="flex items-center gap-2 mb-3">
                        {renamingId === activeFloor.id ? (
                            <form
                                onSubmit={(e) => { e.preventDefault(); commitRename(); }}
                                className="flex items-center gap-2 flex-1"
                                data-testid="rename-floor-form"
                            >
                                <input
                                    autoFocus
                                    value={renameValue}
                                    onChange={(e) => setRenameValue(e.target.value)}
                                    className="flex-1 border border-[#18181B] px-2 py-1 text-sm"
                                    data-testid="rename-floor-input"
                                />
                                <button type="submit" className="font-mono text-[10px] uppercase tracking-[0.18em] bg-[#1FA5CF] text-white px-3 py-1">Save</button>
                                <button type="button" onClick={() => { setRenamingId(null); setRenameValue(''); }} className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#52525B]">Cancel</button>
                            </form>
                        ) : (
                            <>
                                <div className="font-display font-semibold text-lg flex-1 truncate" data-testid="active-floor-name">{activeFloor.name}</div>
                                <button
                                    onClick={() => { setRenamingId(activeFloor.id); setRenameValue(activeFloor.name); }}
                                    data-testid="rename-floor-btn"
                                    className="p-2 hover:bg-[#F4F4F5]" title="Rename"
                                >
                                    <PencilSimple size={16} />
                                </button>
                                <button
                                    onClick={deleteActive}
                                    data-testid="delete-floor-btn"
                                    className="p-2 hover:bg-[#FDECEA] text-[#E53935]" title="Delete floor"
                                >
                                    <Trash size={16} />
                                </button>
                            </>
                        )}
                    </div>
                )}

                {!activeFloor ? (
                    <EmptyState
                        icon={UploadSimple}
                        eyebrow="Plan view"
                        title="Drop your first floorplan"
                        body="Upload one image per floor — ground, first, loft, garden. SVG or high-res PNG/JPEG works best. Once it's up, tap anywhere on the plan to drop a numbered pin and start a defect at that exact spot."
                        cta={{
                            label: 'Upload first floor',
                            icon: Plus,
                            onClick: () => addFileInput.current?.click(),
                            testId: 'empty-add-floor-btn',
                        }}
                        testId="empty-floorplans"
                    />
                ) : (
                    <>
                        <div className="flex items-center gap-2 mb-3">
                            <button
                                onClick={() => { setPlacing(v => !v); setSelectedSnagId(null); }}
                                data-testid="place-pin-btn"
                                className={`flex-1 py-3 font-mono text-xs uppercase tracking-[0.2em] active:scale-95 transition-all duration-150 inline-flex items-center justify-center gap-2 ${placing ? 'bg-[#1FA5CF] text-white' : 'border border-[#18181B] text-[#09090B] hover:bg-[#F4F4F5]'}`}
                            >
                                <MapPin size={16} weight="bold" /> {placing ? 'Tap plan to drop' : 'Drop new pin'}
                            </button>
                        </div>

                        <div
                            ref={imgWrap}
                            onClick={handlePlace}
                            className={`relative border border-[#18181B] bg-[#F4F4F5] ${placing ? 'cursor-crosshair' : ''}`}
                            data-testid="floorplan-canvas"
                        >
                            <img
                                src={activeFloor.image}
                                alt={activeFloor.name}
                                className="w-full block select-none"
                                draggable={false}
                            />
                            {pinnedOnActive.map((s) => {
                                const ring = severityRingColor(s.severity);
                                const pinShape = 'polygon(50% 100%, 100% 45%, 100% 0, 0 0, 0 45%)';
                                const isHighlighted = highlightPinId === s.id;
                                return (
                                    <button
                                        key={s.id}
                                        onClick={(e) => { e.stopPropagation(); setSelectedSnagId(s.id); }}
                                        data-testid={`pin-${s.id}`}
                                        style={{ left: `${s.pin.x}%`, top: `${s.pin.y}%` }}
                                        className={`absolute pin-drop ${isHighlighted ? 'pin-highlight' : ''}`}
                                        title={s.title}
                                    >
                                        {ring ? (
                                            <span
                                                className="flex items-center justify-center"
                                                style={{ padding: '2px', background: ring, clipPath: pinShape }}
                                            >
                                                <span
                                                    className={`flex items-center justify-center w-8 h-8 font-mono text-xs font-bold text-white ${s.status === 'completed' ? 'bg-[#00875A]' : s.builder_response === 'rejected' ? 'bg-[#E53935]' : 'bg-[#1FA5CF]'}`}
                                                    style={{ clipPath: pinShape }}
                                                >{s.pinNumber || '•'}</span>
                                            </span>
                                        ) : (
                                            <span
                                                className={`flex items-center justify-center w-8 h-8 font-mono text-xs font-bold text-white ${s.status === 'completed' ? 'bg-[#00875A]' : s.builder_response === 'rejected' ? 'bg-[#E53935]' : 'bg-[#1FA5CF]'}`}
                                                style={{ clipPath: pinShape }}
                                            >{s.pinNumber || '•'}</span>
                                        )}
                                    </button>
                                );
                            })}
                            {pinnedOnActive.length > 0 && <PinLegend position="bottom-right" testId="floorplan-pin-legend" />}
                        </div>

                        {selectedSnagId && (
                            <div className="mt-3 grid-border p-3 flex items-center justify-between fade-up">
                                <span className="text-sm">Pin selected. Tap plan to move, or remove below.</span>
                                <button
                                    onClick={() => { store.updateSnag(project.id, selectedSnagId, { pin: null }); setSelectedSnagId(null); }}
                                    data-testid="remove-pin-btn"
                                    className="font-mono text-xs uppercase tracking-[0.18em] text-[#E53935] inline-flex items-center gap-1"
                                >
                                    <Trash size={14} /> Remove pin
                                </button>
                            </div>
                        )}

                        <div className="mt-6">
                            <div className="label-tiny mb-2">Pins on this floor ({pinnedOnActive.length}) · Total pinned: {totalPinned}</div>
                            <ul className="divide-y divide-[#E4E4E7] border border-[#E4E4E7]">
                                {pinnedOnActive.length === 0 && <li className="p-4 text-sm text-[#52525B]">No pins on this floor yet. Tap &ldquo;Drop new pin&rdquo;.</li>}
                                {pinnedOnActive.map((s) => (
                                    <li key={s.id} className="p-3 flex items-center gap-3">
                                        <span className={`w-7 h-7 text-white font-mono text-xs font-bold flex items-center justify-center ${s.status === 'completed' ? 'bg-[#00875A]' : s.builder_response === 'rejected' ? 'bg-[#E53935]' : 'bg-[#1FA5CF]'}`}>{s.pinNumber || '•'}</span>
                                        <span className="flex-1 text-sm truncate">{s.title}</span>
                                        <button
                                            type="button"
                                            onClick={() => navigate(`/app/project/${project.id}?snag=${s.id}`)}
                                            data-testid={`floorplan-edit-defect-${s.id}`}
                                            aria-label={`Edit defect ${s.title}`}
                                            title="Open in the property's defect list"
                                            className="font-mono text-[10px] uppercase tracking-[0.18em] underline decoration-[#1FA5CF]"
                                        >Edit</button>
                                        <button
                                            type="button"
                                            onClick={() => { setSelectedSnagId(s.id); setPlacing(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                            data-testid={`floorplan-move-pin-${s.id}`}
                                            className="font-mono text-[10px] uppercase tracking-[0.18em] underline decoration-[#1FA5CF]"
                                        >Move</button>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {unpinned.length > 0 && (
                            <div className="mt-6">
                                <div className="label-tiny mb-2">Unpinned defects ({unpinned.length})</div>
                                <ul className="divide-y divide-[#E4E4E7] border border-[#E4E4E7]">
                                    {unpinned.map(s => (
                                        <li key={s.id} className="p-3 flex items-center gap-3">
                                            <span className="flex-1 text-sm truncate">{s.title}</span>
                                            <button
                                                onClick={() => { setSelectedSnagId(s.id); setPlacing(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                                data-testid={`pin-existing-${s.id}`}
                                                className="font-mono text-[10px] uppercase tracking-[0.18em] bg-[#09090B] text-white px-3 py-2"
                                            >Pin on {activeFloor.name}</button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </>
                )}
            </main>
            <BottomNav />
            {newPinDraft && (
                <PinDetailsModal
                    project={project}
                    snagId={newPinDraft.snagId}
                    onClose={() => setNewPinDraft(null)}
                    onSave={async (patch) => {
                        await store.updateSnag(project.id, newPinDraft.snagId, patch);
                        setNewPinDraft(null);
                    }}
                    onDelete={async () => {
                        await store.deleteSnag(project.id, newPinDraft.snagId);
                        setNewPinDraft(null);
                    }}
                />
            )}
        </div>
    );
}

function PinDetailsModal({ project, snagId, onClose, onSave, onDelete }) {
    const snag = project.snags.find(s => s.id === snagId);
    // The pin gets an auto-generated "Defect at pin N" title when first
    // dropped (so abandoned pins still have a sensible label in the DB).
    // Show that as a placeholder rather than as actual content so the user
    // sees an example to overwrite, not a value to delete first.
    const isAutoTitle = (t, n) => typeof t === 'string' && t.trim() === `Defect at pin ${n}`;
    const [title, setTitle] = useState(isAutoTitle(snag?.title, snag?.pinNumber) ? '' : (snag?.title || ''));
    const [roomId, setRoomId] = useState(snag?.roomId || '');
    const [notes, setNotes] = useState(snag?.notes || '');
    const [photo, setPhoto] = useState(snag?.photo || null);
    const [annotations, setAnnotations] = useState(snag?.photo_annotations || []);
    const [annotatorOpen, setAnnotatorOpen] = useState(false);
    const [photoProcessing, setPhotoProcessing] = useState(false);
    const [saving, setSaving] = useState(false);

    if (!snag) return null;
    const pinNum = snag.pinNumber;

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        setPhotoProcessing(true);
        try {
            // Read as dataURL first, then compress down to a reasonable size
            // for localStorage — avoids the 5 MB silent-quota problem.
            const raw = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(f);
            });
            const compressed = await compressImage(raw, 1400, 0.7);
            setPhoto(compressed);
        } catch (err) {
            console.error('Photo processing failed', err);
            toast.error('Could not read that photo. Try another.');
        } finally {
            setPhotoProcessing(false);
        }
    };

    const handleSave = async () => {
        if (saving || photoProcessing) return;
        setSaving(true);
        try {
            await onSave({ title: title.trim() || `Defect at pin ${pinNum}`, roomId: roomId || null, notes: notes.trim(), photo, photo_annotations: annotations });
            toast.success('Pin saved');
        } catch (err) {
            console.error('Save failed', err);
            const msg = err?.name === 'QuotaExceededError' || /quota/i.test(err?.message || '')
                ? 'Your browser is full. Remove some older photos before adding new ones.'
                : 'Could not save. Please try again.';
            toast.error(msg);
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center no-print" data-testid="pin-details-modal">
            <div className="w-full sm:max-w-md bg-white border border-[#18181B] max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between px-4 py-3 border-b border-[#18181B] sticky top-0 bg-white">
                    <div className="flex items-center gap-3">
                        <span className="w-8 h-8 bg-[#1FA5CF] text-white font-mono text-sm font-bold flex items-center justify-center">{pinNum}</span>
                        <span className="font-display font-semibold">Pin details</span>
                    </div>
                    <button onClick={onClose} data-testid="close-pin-modal" className="p-1 hover:bg-[#F4F4F5]"><X size={18} /></button>
                </div>
                <div className="p-4">
                    <label className="label-tiny">Title</label>
                    <input
                        autoFocus
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={`e.g. Hairline crack — kitchen ceiling (Pin ${pinNum})`}
                        className="w-full mt-1 border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-2 font-display placeholder:text-[#A1A1AA] placeholder:font-normal placeholder:italic"
                        data-testid="pin-title-input"
                    />
                    <label className="label-tiny mt-4 block">Room</label>
                    <select
                        value={roomId || ''}
                        onChange={(e) => setRoomId(e.target.value || null)}
                        className="w-full mt-1 border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-2 bg-white"
                        data-testid="pin-room-select"
                    >
                        <option value="">— Select a room —</option>
                        {project.rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                    <label className="label-tiny mt-4 block">Notes (optional)</label>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                        className="w-full mt-1 border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-2 text-sm"
                        data-testid="pin-notes-input"
                    />
                    <label className="label-tiny mt-4 block">Photo</label>
                    {photoProcessing ? (
                        <div className="mt-1 border border-[#E4E4E7] p-6 text-center bg-[#E8F5FB]" data-testid="pin-photo-processing">
                            <CircleNotch size={24} color="#1FA5CF" weight="bold" className="mx-auto animate-spin" />
                            <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[#1787AB]">Processing photo…</div>
                            <div className="mt-1 text-[10px] text-[#52525B]">Shrinking to fit your device storage</div>
                        </div>
                    ) : (
                        <label className="mt-1 block border border-dashed border-[#E4E4E7] p-4 text-center text-sm text-[#52525B] cursor-pointer hover:bg-[#F4F4F5]">
                            <Camera size={22} className="mx-auto" color="#1FA5CF" weight="duotone" />
                            <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em]">
                                {photo ? 'Retake / change photo' : 'Take / choose photo'}
                            </div>
                            <input data-testid="pin-photo-input" type="file" accept="image/*" className="hidden" onChange={onFile} />
                        </label>
                    )}
                    {photo && !photoProcessing && (
                        <div className="mt-2 relative">
                            <img src={photo} alt="preview" className="w-full max-h-56 object-cover border border-[#E4E4E7]" />
                            <button
                                onClick={() => setAnnotatorOpen(true)}
                                data-testid="pin-annotate-btn"
                                className="absolute top-2 left-2 inline-flex items-center gap-1 bg-[#1FA5CF] text-white px-2 py-1 font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-[#1787AB]"
                            >
                                <PencilSimple size={10} weight="bold" /> {annotations?.length ? `Edit (${annotations.length})` : 'Mark up'}
                            </button>
                            <button
                                onClick={() => { setPhoto(null); setAnnotations([]); }}
                                data-testid="pin-remove-photo-btn"
                                className="absolute top-2 right-2 bg-white/90 border border-[#E4E4E7] px-2 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#E53935]"
                            >Remove</button>
                        </div>
                    )}
                    <div className="flex items-center gap-2 mt-4">
                        <button
                            onClick={handleSave}
                            disabled={saving || photoProcessing}
                            data-testid="save-pin-btn"
                            className={`flex-1 text-white py-3 font-mono text-xs uppercase tracking-[0.2em] active:scale-95 transition-all duration-150 inline-flex items-center justify-center gap-2 ${saving || photoProcessing ? 'bg-[#1FA5CF]/60 cursor-wait' : 'bg-[#1FA5CF] hover:bg-[#1787AB]'}`}
                        >
                            {saving ? <><CircleNotch size={14} weight="bold" className="animate-spin" /> Saving…</> : photoProcessing ? 'Photo processing…' : 'Save'}
                        </button>
                        <button
                            onClick={() => {
                                const t = toast('Remove this pin and its defect?', {
                                    description: 'The pin will be deleted from the floorplan and the defect removed entirely.',
                                    duration: 8000,
                                    action: { label: 'Remove', onClick: () => onDelete() },
                                    cancel: { label: 'Cancel', onClick: () => toast.dismiss(t) },
                                });
                            }}
                            disabled={saving}
                            data-testid="delete-pin-btn"
                            className="px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] border border-[#E53935] text-[#E53935] hover:bg-[#FDECEA] active:scale-95 transition-all duration-150"
                        >Discard</button>
                    </div>
                </div>
            </div>
            {annotatorOpen && photo && (
                <PhotoAnnotator
                    photoSrc={photo}
                    initialAnnotations={annotations}
                    onCancel={() => setAnnotatorOpen(false)}
                    onSave={(strokes) => { setAnnotations(strokes); setAnnotatorOpen(false); }}
                />
            )}
        </div>
    );
}
